import { EnrollmentProfilesList } from "@/components/enrollment/EnrollmentProfilesList";
import { useAdeProfiles } from "@/hooks/useAdeProfiles";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { Input } from "@/components/ui/input";
import ViewToggle from "@/components/ViewToggle";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function EnrollmentProfilesPage() {
  // Fetch all profiles directly without needing ABM connection first
  const { profiles: adeProfiles, loading, deleteProfile } = useAdeProfiles();

  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("enrollmentProfilesViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteTarget = adeProfiles.find((p) => p.id === deleteTargetId) || null;

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    try {
      setIsDeleting(true);
      await deleteProfile(deleteTargetId);
      setDeleteTargetId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    localStorage.setItem("enrollmentProfilesViewPreference", view);
  }, [view]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <div className="text-left mb-8">
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6 space-y-4">
                <div className="h-6 w-2/3 bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse"></div>
                <div className="flex justify-between pt-4">
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6 space-y-6">
        {/* Header */}
        <PageHeader title="Enrollment Profiles" description="Manage Apple Business Manager enrollment profiles for automated device enrollment">
          <Button asChild>
            <Link to="/admin/enrollment-profiles/new">
              <Plus className="mr-2 h-4 w-4" />
              New Enrollment Profile
            </Link>
          </Button>
        </PageHeader>

        {/* Filters and Stats */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search enrollment profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-72"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{adeProfiles.length}</Badge>
                <span>Total</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="default">{adeProfiles.filter(p => p.is_mandatory).length}</Badge>
                <span>Mandatory</span>
              </div>
            </div>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>

        {adeProfiles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Inbox className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No Enrollment Profiles</h3>
            <p className="text-muted-foreground max-w-md mb-4">
              Create your first enrollment profile to configure automated device enrollment via Apple Business Manager.
            </p>
            <Button asChild>
              <Link to="/admin/enrollment-profiles/new">
                <Plus className="mr-2 h-4 w-4" />
                New Enrollment Profile
              </Link>
            </Button>
          </div>
        ) : (
          <EnrollmentProfilesList
            profiles={adeProfiles}
            view={view}
            searchQuery={searchQuery}
            onDelete={(id) => setDeleteTargetId(id)}
          />
        )}
      </div>

      <AlertDialog
        open={!!deleteTargetId || isDeleting}
        onOpenChange={(open) => {
          if (!open && !isDeleting) setDeleteTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Enrollment Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete
              {deleteTarget ? ` "${deleteTarget.profile_name}"` : " this enrollment profile"}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Prevent Radix from auto-closing the dialog so the in-flight
                // "Deleting..." state is visible until deleteProfile resolves.
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete Profile"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
